import { FC, ReactNode, useId, useState } from 'react';
import clsx from 'clsx';
import Button from '../../atoms/Button';
import type { ButtonTone, ButtonVariant } from '../../atoms/Button';
import { confirmVariantOf } from '../../atoms/Button/confirmVariant';
import Modal from '../../atoms/Modal/ModalView';
import Hint from '../../atoms/Hint';
import ModalLoadingOverlay from '../../atoms/ModalLoadingOverlay';
import type { ModalSize } from '../../types/ModalSize';
import { stringifyError } from '@srm/core/errors';
import { joinIds } from '../../context/FieldA11yContext';
import { sanitizeMessageHtml } from '../../lib/html';
import { MessageToneHeader, MessageToneList } from '../MessageTone';
import type { MessageTone } from '../MessageTone';

const isPromiseLike = (value: unknown): value is PromiseLike<void> =>
    typeof (value as PromiseLike<void> | undefined)?.then === 'function';

export interface IMessageDialogProps {
    header: string;
    /** Иконка тона в шапке, текст — по заголовку. По умолчанию — `danger` у окна с `okButtonTone="danger"`, иначе без
     *  иконки. */
    tone?: MessageTone;
    /** Текст окна — HTML, очищается DOMPurify. Пустой или из пробелов — окно без тела. */
    message?: string;
    /** Несколько сообщений (HTML) нумерованным списком под `message`. */
    messagesHtml?: string[];
    /** Содержимое под сообщениями: поля, таблица, пояснения. Вопрос в нём (разметка с `Kbd`, список) — элемент с id
     *  в `aria-describedby`. */
    children?: ReactNode;
    /** id описания окна в `children` (через пробел — несколько) — к тексту `message` и `messagesHtml`, которые окно
     *  описывают сами. */
    'aria-describedby'?: string;
    size?: ModalSize;
    /** @default true */
    showOkButton?: boolean;
    /** @default true */
    showCancelButton?: boolean;
    okButtonText?: string;
    cancelButtonText?: string;
    /** Вид кнопки «ОК» @default primary, с `okButtonTone` `success`/`danger` — light */
    okButtonVariant?: ButtonVariant;
    /** Тон кнопки «ОК» (`ButtonTone`): необратимое действие — `okButtonTone="danger"` (вид по умолчанию — `light`) */
    okButtonTone?: ButtonTone;
    cancelButtonVariant?: ButtonVariant;
    /**
     * Нажатие «ОК». Если вернулся промис, до его завершения кнопки недоступны и окно не закрыть. Окно не
     * закрывается само ни после успеха, ни после отказа — закрывает вызывающий; отказ промиса не гасится.
     */
    onConfirm: () => void | Promise<void>;
    onCancel: () => void;
    loading?: boolean;
    /**
     * Отказ операции: строка либо то, чем отклонил интерцептор (массив строк с прикреплённым
     * кодом обращения). Десятки модалок кладут сюда `error` из catch как есть — разбирать и
     * дописывать код обращения обязан рендер, иначе он терялся при склейке в строку.
     */
    errorText?: unknown;
    successText?: string;
    /** Кнопка, на которой фокус при открытии. По умолчанию — «Отмена», без неё — «ОК»: окно чаще всего
     *  спрашивает перед удалением, и Enter, нажатый дважды, не должен выполнять действие. */
    initialFocus?: 'ok' | 'cancel';
}

const MessageDialog: FC<IMessageDialogProps> = ({
    size = 'm',
    message = '',
    messagesHtml = [],
    children,
    showOkButton = true,
    showCancelButton = true,
    okButtonText = 'ОК',
    cancelButtonText = 'Отмена',
    okButtonVariant,
    okButtonTone,
    cancelButtonVariant = 'light',
    initialFocus,
    tone: toneProp,
    'aria-describedby': describedByProp,
    ...props
}: IMessageDialogProps) => {
    const tone = toneProp ?? (okButtonTone === 'danger' ? 'danger' : undefined);
    const titleId = useId();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const canCancel = showCancelButton && !props.loading;
    const canOk = showOkButton && !props.loading;
    const focusOk = canOk && (initialFocus === 'ok' || !canCancel);
    const focusCancel = canCancel && !focusOk;
    // Код обращения — последней строкой: Hint сохраняет переводы строк (pre-wrap).
    const errorMessage = props.errorText ? stringifyError(props.errorText) : '';
    const hasMessage = message.trim().length > 0;
    const hasMessagesHtml = messagesHtml.length > 0;
    const hasChildren = children !== undefined && children !== null && typeof children !== 'boolean';
    const hasBody =
        hasMessage || hasMessagesHtml || hasChildren || Boolean(props.errorText || props.successText || props.loading);
    // описание окна — текст вопроса (поля и таблицы под ним — нет)
    const textId = useId();
    const describedBy = joinIds(
        hasMessage && `${textId}-message`,
        ...messagesHtml.map((_, index) => `${textId}-list-${index}`),
        describedByProp,
    );

    const onOk = () => {
        const result = props.onConfirm();
        if (!isPromiseLike(result)) return;
        setIsSubmitting(true);
        void Promise.resolve(result).finally(() => {
            setIsSubmitting(false);
        });
    };

    return (
        <Modal
            className={clsx('modal-dlg-container')}
            size={size}
            // крестик, Escape и фон — то же, что «Отмена»; пока окно занято или идёт выполнение, закрыть нельзя
            onClose={props.onCancel}
            closeDisabled={isSubmitting || Boolean(props.loading)}
            header={tone ? <MessageToneHeader tone={tone} title={props.header} id={titleId} /> : props.header}
            aria-labelledby={tone ? titleId : undefined}
            aria-describedby={describedBy}
            footer={
                <div className="modal-dlg-buttons">
                    {showOkButton && (
                        <Button
                            size="sm"
                            variant={okButtonVariant ?? confirmVariantOf(okButtonTone)}
                            tone={okButtonTone}
                            aria-label={okButtonText}
                            data-autofocus={focusOk || undefined}
                            onClick={onOk}
                            loading={isSubmitting}
                            disabled={isSubmitting || props.loading}
                        >
                            {okButtonText}
                        </Button>
                    )}
                    {showCancelButton && (
                        <Button
                            variant={cancelButtonVariant}
                            size="sm"
                            aria-label={cancelButtonText}
                            data-autofocus={focusCancel || undefined}
                            onClick={() => {
                                if (props?.onCancel) props?.onCancel();
                            }}
                            disabled={isSubmitting || props.loading}
                        >
                            {cancelButtonText}
                        </Button>
                    )}
                </div>
            }
        >
            {hasBody ? (
                <ModalLoadingOverlay visible={!!props.loading}>
                    <div className={tone ? 'message-tone__body' : undefined}>
                        {/* отклик на действие — объявляется при появлении */}
                        {errorMessage && (
                            <Hint title={`Ошибка: ${errorMessage}`} tone="danger" maxWidth="100%" role="alert" />
                        )}
                        {props.successText && (
                            <Hint title={`${props.successText}`} tone="success" maxWidth="100%" role="alert" />
                        )}
                        {hasMessage && (
                            <div
                                id={`${textId}-message`}
                                className="modal-dlg-text"
                                dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(message) }}
                            />
                        )}
                        <MessageToneList messages={messagesHtml} idPrefix={`${textId}-list`} />
                        {hasChildren && <div className="modal-dlg-body">{children}</div>}
                    </div>
                </ModalLoadingOverlay>
            ) : null}
        </Modal>
    );
};

export default MessageDialog;
